import React, { useMemo, useCallback, useState } from 'react';
import { Alert, Pressable, RefreshControl, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ConfigurationDeleteButton } from '../../components/ConfigurationDeleteButton';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { getOrganizationModuleCapabilities } from '../../config/organizationCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useConfigurationSoftDelete } from '../../hooks/useConfigurationSoftDelete';
import { getEmployees } from '../../services/employeeService';
import {
  addTeamMember,
  deactivateTeam,
  getTeamDetails,
  getTeamMembers,
  removeTeamMember,
} from '../../services/teamService';
import type { TeamDetails, TeamMember } from '../../types/team';
import { isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { toCalendarDateString } from '../../utils/dateTime';
import { organizationErrorMessage } from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'TeamDetails'>;

function Field({
  label,
  value,
  palette,
}: {
  label: string;
  value: string | null;
  palette: ColorPalette;
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: palette.textSecondary }]}>
        {label}
      </Text>
      <Text style={[styles.fieldValue, { color: palette.textPrimary }]}>
        {value || 'Not listed'}
      </Text>
    </View>
  );
}

export function TeamDetailsScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      section: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        marginBottom: spacing.sm,
        marginTop: spacing.md,
      },
      memberName: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      memberMeta: { ...typography.caption, color: palette.textSecondary },
      remove: { ...typography.caption, color: palette.error, fontWeight: '600' as const },
      help: { ...typography.caption, color: palette.textMuted },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getOrganizationModuleCapabilities(auth.permissions);
  const { teamId } = route.params;
  const [item, setItem] = useState<TeamDetails | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<SelectorOption | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const softDelete = useConfigurationSoftDelete();

  const load = useCallback(
    async (isRefresh = false) => {
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const [data, teamMembers] = await Promise.all([
          getTeamDetails(teamId),
          getTeamMembers(teamId).catch(() => [] as TeamMember[]),
        ]);
        setItem(data);
        setMembers(teamMembers);
        setError(null);
      } catch (caught) {
        setItem(null);
        setError(
          organizationErrorMessage(
            caught,
            'The requested record could not be loaded.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [teamId],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  async function loadEmployees(search: string): Promise<SelectorOption[]> {
    const result = await getEmployees({
      page: 1,
      pageSize: 20,
      filters: { search },
    });
    const existing = new Set(members.map(member => member.employeeId));
    return result.items
      .filter(employee => !existing.has(employee.employeeId))
      .map(employee => ({
        id: employee.employeeId,
        label: employee.displayName,
        subtitle: employee.employeeNumber,
      }));
  }

  async function addMember() {
    if (!selectedEmployee || saving) return;
    setSaving(true);
    try {
      const next = await runOperation(
        'add',
        () =>
          addTeamMember(teamId, selectedEmployee.id, toCalendarDateString(new Date())),
        {
          failureDetail: caught =>
            organizationErrorMessage(
              caught,
              'The employee could not be added to this team.',
            ),
        },
      );
      setMembers(next);
      setSelectedEmployee(null);
    } catch {
      // runOperation already showed the "Add failed" popup.
    } finally {
      setSaving(false);
    }
  }

  function confirmRemove(member: TeamMember) {
    if (member.isTeamLead) {
      Alert.alert(
        'Team lead',
        'Assign a new team lead before removing this employee from the team.',
      );
      return;
    }
    Alert.alert(
      'Remove team member',
      `Remove ${member.displayName} from this team?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            runOperation('delete', () => removeTeamMember(teamId, member.employeeId), {
              failureDetail: caught =>
                organizationErrorMessage(
                  caught,
                  'The employee could not be removed from this team.',
                ),
            })
              .then(setMembers)
              .catch(() => undefined);
          },
        },
      ],
    );
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.TEAM_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Team"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading team" />
        </AppScreen>
      ) : error && !item ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Team"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
        </AppScreen>
      ) : item ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => load(true)}
            />
          }
        >
          <ScreenHeader
            title={item.teamName}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {capabilities.canUpdateTeam &&
            isActiveConfigurationRecord(item.isActive) ? (
              <ConfigurationDeleteButton
                entityLabel="team"
                loading={softDelete.busy}
                disabled={softDelete.busy}
                onPress={() =>
                  softDelete.confirmAndDelete({
                    entityLabel: 'team',
                    recordName: item.teamName,
                    deleteRecord: async () => {
                      await deactivateTeam(item);
                    },
                    onSuccess: () => navigation.goBack(),
                    errorMessage: caught =>
                      organizationErrorMessage(
                        caught,
                        'Unable to delete team.',
                      ),
                  })
                }
              />
            ) : null}
            <Field palette={palette} label="Code" value={item.teamCode} />
            <Field palette={palette} label="Description" value={item.description} />
            <Field palette={palette} label="Department" value={item.departmentName} />
            <Field palette={palette} label="Location" value={item.locationName} />
            <Field palette={palette} label="Team lead" value={item.teamLeadName} />
            <Field palette={palette} label="Status" value={item.statusLabel} />
          </AppCard>

          <Text style={themed.section}>Team members</Text>
          <Text style={themed.help}>
            Member requests go to this team’s lead and HR. The team lead’s
            requests go to their reporting manager and HR.
          </Text>
          {capabilities.canUpdateTeam ? (
            <AppCard style={styles.card}>
              <SearchableEntitySelector
                label="Add employee"
                selectedValue={selectedEmployee?.id ?? null}
                selectedLabel={selectedEmployee?.label ?? null}
                loadOptions={loadEmployees}
                onSelect={setSelectedEmployee}
                onClear={() => setSelectedEmployee(null)}
              />
              <AppButton
                title="Add to team"
                onPress={addMember}
                loading={saving}
                disabled={!selectedEmployee || saving}
              />
            </AppCard>
          ) : null}
          <AppCard style={styles.card}>
            {members.length === 0 ? (
              <Text style={themed.help}>No employees are assigned to this team yet.</Text>
            ) : (
              members.map(member => (
                <View
                  key={member.employeeId}
                  style={[styles.memberRow, { borderBottomColor: palette.border }]}
                >
                  <View style={styles.memberMain}>
                    <Text style={themed.memberName}>{member.displayName}</Text>
                    <Text style={themed.memberMeta}>
                      {member.isTeamLead ? 'Team lead' : 'Member'}
                      {member.employeeNumber ? ` · ${member.employeeNumber}` : ''}
                      {member.reportingManagerName
                        ? ` · RM: ${member.reportingManagerName}`
                        : ''}
                    </Text>
                  </View>
                  {capabilities.canUpdateTeam && !member.isTeamLead ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Remove ${member.displayName}`}
                      onPress={() => confirmRemove(member)}
                    >
                      <Text style={themed.remove}>Remove</Text>
                    </Pressable>
                  ) : null}
                </View>
              ))
            )}
          </AppCard>

          <View style={styles.actions}>
            {capabilities.canUpdateTeam ? (
              <AppButton
                title="Edit"
                onPress={() =>
                  navigation.navigate(routes.teamEdit, { teamId })
                }
              />
            ) : null}
          </View>
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.md },
  field: { gap: spacing.xs },
  fieldLabel: {
    ...typography.caption,
    fontWeight: '600',
  },
  fieldValue: { ...typography.body },
  actions: { gap: spacing.sm, marginTop: spacing.lg },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
  },
  memberMain: { flex: 1, gap: 2 },
});
